import React, { useEffect, useState } from 'react';
import { get } from '../utils/apiClient';
import { getCachedData, setCachedData, isCacheStale } from '../utils/cacheUtils';
import LoadingSpinner from '../components/LoadingSpinner';
import Base64Image from '../components/Base64Image';
import PageHeader from '../components/PageHeader';
import {
  GalleryPageContainer,
  Section,
  SectionTitle,
  TextBlock,
  GalleryGrid,
  GalleryCard,
  GalleryTitle,
  GalleryMeta,
  GalleryDescription,
} from '../styles/GalleryPageStyles';

const CACHE_KEY = 'gallery_data';
const MAX_AGE_MS = 1000 * 60 * 60;

const GalleryPage = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      const cached = await getCachedData(CACHE_KEY);

      if (cached && !isCacheStale(cached, MAX_AGE_MS)) {
        if (!isMounted) return;
        setPageData(cached.data);
        setLoading(false);
      }

      try {
        const data = await get('/gallery');
        if (!isMounted) return;
        setPageData(data);
        setCachedData(CACHE_KEY, data);
        setLoading(false);
      } catch (err) {
        if (!isMounted) return;
        if (!cached) {
          setLoading(false);
        }
        setError(err && err.message ? err.message : 'Failed to load gallery data.');
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  if (loading && !pageData) {
    return (
      <GalleryPageContainer>
        <LoadingSpinner />
      </GalleryPageContainer>
    );
  }

  if (error && !pageData) {
    return <GalleryPageContainer>{error}</GalleryPageContainer>;
  }

  const header = pageData && pageData.header ? pageData.header : null;
  const description = pageData ? pageData.description : null;
  const images = pageData && Array.isArray(pageData.images) ? pageData.images : [];

  const groups = images.reduce((acc, item) => {
    const key = item.albumName || item.category || 'General';
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});

  Object.keys(groups).forEach((key) => {
    groups[key].sort((a, b) => {
      const ao = typeof a.displayOrder === 'number' ? a.displayOrder : 0;
      const bo = typeof b.displayOrder === 'number' ? b.displayOrder : 0;
      return ao - bo;
    });
  });

  const groupKeys = Object.keys(groups);

  return (
    <GalleryPageContainer>
      {header && (
        <PageHeader
          title={header.title}
          image={header.backgroundImage}
        />
      )}

      {description && (
        <Section>
          <SectionTitle>Description</SectionTitle>
          <TextBlock>{description}</TextBlock>
        </Section>
      )}

      {groupKeys.map((key) => (
        <Section key={key}>
          <SectionTitle>{key}</SectionTitle>
          <GalleryGrid>
            {groups[key].map((item, index) => (
              <GalleryCard key={`${item.title || 'image'}-${index}`}>
                {item.image && (
                  <Base64Image
                    image={item.image}
                    alt={item.title || key}
                    style={{ width: '100%', borderRadius: 6, marginBottom: 6 }}
                  />
                )}
                {item.title && <GalleryTitle>{item.title}</GalleryTitle>}
                {item.albumName && (
                  <GalleryMeta>Album: {item.albumName}</GalleryMeta>
                )}
                {item.category && (
                  <GalleryMeta>Category: {item.category}</GalleryMeta>
                )}
                {item.description && (
                  <GalleryDescription>{item.description}</GalleryDescription>
                )}
              </GalleryCard>
            ))}
          </GalleryGrid>
        </Section>
      ))}
    </GalleryPageContainer>
  );
};

export default GalleryPage;
